// src/core/router.js — router hash y bootstrap de la app.
//
// Extraido de js/app.js (sub-fase 3.5 del refactor).
//
// Movido en 3.5: navigate, router. (loadFixtureSandbox se movió a
// ../modules/fixture.js en sub-fase 3.9 — resolución DT-2.)
//
// Bootstrap: este modulo se autoorquesta — registra el listener de
// hashchange y dispara router() contra DOMContentLoaded. En la sub-fase
// 3.15 (src/main.js) ese bootstrap se mueve al entry-point central y
// router.js queda como modulo puro (solo exports).
//
// Dependencias salientes:
//   - de ./groups.js (sub-fase 3.6): getActiveGroupCode,
//     hasValidActiveGroupSession, resetMockTwoGroups, MOCK_TWO_GROUP_CODES
//   - de ../modules/fixture.js (sub-fase 3.9): loadFixtureSandbox
//   - de js/app.js (lazy via window.*, hasta que cada render se modularice
//     en 3.11-3.14): appRoot, renderLanding, renderIngreso, renderCrearGrupo,
//     renderGrupo, renderRanking, renderPacto, renderRitualesGrupo,
//     renderTribuna, renderTribunaArengas, renderTribunaCabalas,
//     renderTribunaPromesas, renderPerfil
//
// La lectura via window.* es LAZY (dentro del cuerpo de cada funcion), no
// top-level. Eso garantiza que cuando se invoca router(), js/app.js (defer)
// ya termino de ejecutar y todos los simbolos viven en window.

import {
  getActiveGroupCode,
  hasValidActiveGroupSession,
  getActiveMembershipsForUser,
  setActiveGroupCode,
} from './groups.js'
import { loadFixtureSandbox } from '../modules/fixture.js'
import { renderProde } from '../modules/prode.js'
import { appRoot } from '../utils/dom.js'
import { renderPerfilProde } from '../modules/perfil-prode.js'
import { renderPerfilTribuna } from '../modules/perfil-tribuna.js'
import { renderDevSandbox } from '../modules/dev-sandbox.js'
import { renderPanelCupones, PANEL_ROUTE } from '../modules/panel-cupones.js'

// ─── navigate ───────────────────────────────────────────────────────────────

export const navigate = (path) => {
  window.location.hash = path
}

// ─── router ─────────────────────────────────────────────────────────────────

export const router = () => {
  document.querySelectorAll('#active-group-selector').forEach(selector => selector.remove())
  window.scrollTo(0, 0)
  const hash = window.location.hash.slice(1) || (window.location.pathname === '/fixture-sandbox' ? '/fixture-sandbox' : '/')
  appRoot.innerHTML = ''

  if (hash === '/') {
    // 1. localStorage tiene grupo activo válido → entra derecho.
    if (hasValidActiveGroupSession()) {
      window.renderGrupo(getActiveGroupCode())
      return
    }
    // 2. Sin localStorage pero hay grupos en cache (device nuevo / primer login)
    //    → auto-seleccionar el primero y entrar. Sin esto, el user con N grupos
    //    quedaba atascado en landing porque renderLanding no ofrece forma de
    //    entrar a grupos existentes. Cierra DT-DC (23-may noche-2).
    const grupos = getActiveMembershipsForUser()
    if (grupos.length > 0) {
      setActiveGroupCode(grupos[0].code)
      window.renderGrupo(grupos[0].code)
      return
    }
    // 3. Sin grupos en ningún lado → landing real.
    window.renderLanding()
  }
  else if (hash === '/ingreso') window.renderIngreso()
  else if (hash === '/fixture-sandbox') loadFixtureSandbox()
  else if (hash === '/dev-sandbox') renderDevSandbox()
  else if (hash === PANEL_ROUTE) renderPanelCupones()
  else if (hash === '/crear-grupo') window.renderCrearGrupo()
  else if (hash.startsWith('/g/')) {
    const codigo = hash.split('/')[2].toUpperCase()
    window.renderGrupo(codigo)
  } else if (hash.startsWith('/ranking/')) {
    const codigo = hash.split('/')[2].toUpperCase()
    window.renderRanking(codigo)
  } else if (hash.startsWith('/pacto/')) {
    const codigo = hash.split('/')[2].toUpperCase()
    window.renderPacto(codigo)
  } else if (hash === '/tribuna') {
    // Link público a la Tribuna entera (28-may-2026): sin código de grupo,
    // sin arenga destacada. renderTribunaPublica() sin args lo maneja como
    // modo visitante puro — los handlers ya saben distinguir currentUser/null.
    window.renderTribunaPublica()
  } else if (hash.startsWith('/tribuna/')) {
    const codigo = hash.split('/')[2].split('?')[0].toUpperCase()
    window.renderTribuna(codigo)
  } else if (hash.startsWith('/t/')) {
    // FASE 3.17 · Bloque D — link viral pactomundial.futbol/t/AUDIO_ID.
    // El audio_id es URL-safe [A-Za-z0-9], 8-12 chars: NO .toUpperCase().
    const audioId = hash.split('/')[2].split('?')[0]
    window.renderTribunaPublica(audioId)
  } else if (hash.startsWith('/tribuna-arengas/')) {
    const codigo = hash.split('/')[2].toUpperCase()
    window.renderTribunaArengas(codigo)
  } else if (hash.startsWith('/tribuna-cabalas/')) {
    const codigo = hash.split('/')[2].toUpperCase()
    window.renderTribunaCabalas(codigo)
  } else if (hash.startsWith('/tribuna-promesas/')) {
    const codigo = hash.split('/')[2].toUpperCase()
    window.renderTribunaPromesas(codigo)
  } else if (hash.startsWith('/rituales/')) {
    const parts = hash.split('/')
    const codigo = parts[2].toUpperCase()
    const tab = parts[3] || 'arengas'
    window.renderRitualesGrupo(codigo, tab)
  } else if (hash.startsWith('/prode/')) {
    const parts = hash.split('/')
    renderProde(parts[2].toUpperCase(), parts[3])
  } else if (hash.startsWith('/perfil/')) {
    const codigo = hash.split('/')[2].toUpperCase()
    window.renderPerfil(codigo)
  } else if (hash.startsWith('/perfil-prode/')) {
    const codigo = hash.split('/')[2].toUpperCase()
    renderPerfilProde(codigo)
  } else if (hash.startsWith('/perfil-tribuna/')) {
    const codigo = hash.split('/')[2].toUpperCase()
    renderPerfilTribuna(codigo)
  } else {
    window.renderLanding()
  }
}

// ─── Bridge transitorio a window.* (sub-fase 3.5 → 3.15) ───────────────────
// window.router eliminado en 3.15: cero consumidores externos verificados con
// `grep -rn "window.router"` sobre el repo (solo producía aquí, ningún
// `window.router(...)` consumer en src/, js/, index.html).
// window.navigate se mantiene por paridad transitoria — eventual limpieza
// si scouting confirma cero consumers en HTML strings dinámicos.

if (typeof window !== 'undefined') {
  window.navigate           = navigate
}

// ─── Bootstrap movido a src/main.js en sub-fase 3.15 ────────────────────────
// El registro del listener hashchange y el dispatch inicial de router()
// contra DOMContentLoaded ahora viven en main.js (entry-point del bundle).
// Este módulo queda como módulo puro de exports (navigate, router).
//
// FIFO de hashchange listeners post-3.15 (preservado, DT-5 documentado):
//   1. patchProfileRoute (index.html:718, IIFE inline, ejecutado durante
//      parsing del HTML — el primero en registrar el listener).
//   2. router (main.js, módulo deferred — ejecutado POST-parsing del HTML).
// patchProfileRoute corre primero por construcción FIFO. Polling con rAF
// (~130ms, 8 frames) en patchProfileRoute bridge la ventana hasta que
// router termine de pintar la vista en hashchange.
